import { apiRoot, isProduction, localDev, serverCodes, fullAppSuiteRootURL, currentEnv } from "../data/constants.ts";
import { liveAppData } from "../store/store.ts";
import { devlog } from "./utils.ts";

let moduleIsAuthenticated = false
let _getUserDataInFlight: Promise<AuthResponse> | null = null;

// Always include credentials so session cookies are sent cross-origin (e.g. localhost → staging)
export const apiFetch = (url: string, init?: RequestInit) =>
  fetch(url, { credentials: 'include', ...init });

/**
 * Fetches a static JSON resource (config, settings, lang)
 * @param path - Path relative to app suite root or absolute URL
 * @returns Promise<T | null> - The parsed JSON data, or null if 404 or other error
 */
export const fetchResource = async <T = any>(path: string): Promise<T | null> => {
  const url = path.startsWith('http') || path.startsWith('/') || path.startsWith('./')
    ? path 
    : `${fullAppSuiteRootURL}/${path}`;
    
  try {
    const response = await fetch(url);
    if (response.status === 404) return null;
    if (!response.ok) {
      return null;
    }
    return await response.json();
  } catch (err) {
    return null;
  }
};

/**
 * Validates server response by checking status code and parsing JSON
 * @param response - The fetch Response object
 * @returns Promise<any> - The parsed JSON response
 * @throws Error - If status is not 200, response is empty, or JSON parsing fails
 */
const validatedServerResponse = async (response: Response): Promise<any> => {
  // Check if response status is 200
  if (response.status !== 200) {
    throw new Error(`Server returned status ${response.status}: ${response.statusText}`);
  }

  // Check if response has content
  const responseText = await response.text();
  devlog("[API]", responseText)
  if (!responseText || responseText.trim() === '') {
    throw new Error('Server returned empty response');
  }

  // Parse JSON
  try {
    const jsonData = JSON.parse(responseText);
    return jsonData;
  } catch (parseError) {
    throw new Error(`[API] (Validating response) Failed to parse server response as JSON: ${responseText.slice(0, 200)}...`);
  }
};

// export const getUserData =()=> fetch(`${apiRoot}?function=getUserData`)


// Define an interface for the expected JSON response
interface LoginResponse {
  success: boolean;
  errorMessage?: string;
  data?: any[];
}

// Lambda function for handling the login
export const doLogin = async (form: HTMLFormElement): Promise<LoginResponse> => {
  try {
    // Create FormData directly from the form element
    const formData = new FormData(form);
    const response = await apiFetch(`${apiRoot}?function=login`, {
      method: 'POST',
      body: formData,
    });

    // Validate and parse the JSON from the response
    const jsonResponse = await validatedServerResponse(response) as LoginResponse;

    // Check if the response indicates success
    if (!jsonResponse.success) {
      return {
        success: false,
        errorMessage: jsonResponse.errorMessage || 'Login failed'
      };
    }

    // Return the successful response
    return jsonResponse;
  } catch (error) {
    console.error(error.message);

    // Return a fallback LoginResponse in case of an error
    return {
      success: false,
      errorMessage: error.message || 'An error occurred during login.'
    };
  }
};

export const doLogout = async () => {
  try {
    const response = await apiFetch(`${apiRoot}?function=logout`, { method: 'POST' });
    const data = await validatedServerResponse(response);

    if (!data.success) {
      throw new Error(data.errorMessage || 'Logout failed');
    }

    return data;
  } catch (error) {
    console.error('Logout error:', error.message);
    throw error;
  }
}


// import { commonAppData } from '../data/store.ts';

export interface UserData {
  UUID: string;
  firstName: string;
  lastName: string;
  eMail: string;
  license: {
    startDate: string | null;
    endDate: string | null;
    type: string | null;
    /** Number of licensed seats; 0 = no restriction */
    seats?: number;
    applications?: string[];
  };
  organization: string;
  /** @deprecated use environment instead */
  organizationId?: number;
  environment?: string;
}

export interface AuthResponse {
  isAuthenticated: boolean;
  userData: UserData | null;
  isDummyData?: boolean;
  message?: string;
  success?: boolean;
  errorCode?: string | number;
}

/**
 * Signs in a user with the provided form data, then gets user data if successful.
 * @param formData - Form data containing email and password
 * @returns Promise with the authentication response
 */
export const signIn = async (formData: FormData): Promise<AuthResponse> => {
  liveAppData.pendingAuth = true;

  window.dispatchEvent(new CustomEvent('as-auth-sign-in-started', {
    bubbles: true,
    composed: true,
    detail: {
      formData
    }
  }));

  try {
    const response = await apiFetch(`${apiRoot}?function=login`, {
      method: 'POST',
      body: formData,
    });

    const data = await validatedServerResponse(response);
    // devlog('signIn data', data);

    // Check if the response indicates success
    if (!data.success) {
      return {
        isAuthenticated: false,
        userData: null,
        message: data.message || data.errorMessage || 'Sign in failed',
        success: false,
        errorCode: data.errorCode
      };
    }

    // If authentication is successful, get user data and update store
    const userDataResponse = await getUserData();
    return userDataResponse;
  } catch (error) {
    console.error('signIn error:', error.message);
    return {
      isAuthenticated: false,
      userData: null,
      message: error.message || 'Network error occurred',
      success: false,
      errorCode: '0' // UNKNOWN_ERROR
    };
  } finally {
    liveAppData.pendingAuth = false;
  }
};

export type DefaultLogEventStrings =
  'sign_in'
  | 'sign_out'
  | 'app_opened'
  | 'new_session'
  | 'import_mesh'
  | 'export_mesh'
  | 'restore_session_zip'
  | 'save_session_zip'
  | 'transfer_mesh'
  | 'auto_restore_session' // later?

export const sendLogToServer = async (event: string, data?: any, subEvent?: string) => {
  const appKey = liveAppData.appId;
  //TODO: test
  if (localDev) return
  if (appKey === undefined) {
    console.error('appKey is undefined');
    return Promise.reject(new Error('appKey is undefined'));
  }
  const formData = new FormData();
  formData.append('event', event);
  formData.append('application', appKey);
  if (subEvent) {
    formData.append('subEvent', subEvent);
  }
  if (data) {
    formData.append('dataString', JSON.stringify(data));
  }
  devlog('sendLogEvent', event, appKey);
  const response = await apiFetch(`${apiRoot}?function=logEvent`, {
    method: 'POST',
    body: formData
  })
  return requestSucceeded(response);
}

/**
 * Sends a log event to the server using navigator.sendBeacon (ideal for unload events)
 * @param event - Event name string
 * @param data - Optional data object to log
 */
export const sendBeaconToServer = (event: string, data?: any): boolean => {
  const appKey = liveAppData.appId;
  if (localDev || !appKey) return false;

  const formData = new FormData();
  formData.append('event', event);
  formData.append('application', appKey);
  if (data) {
    formData.append('dataString', JSON.stringify(data));
  }
  return navigator.sendBeacon(`${apiRoot}?function=logEvent`, formData);
};

/**
 * Alternative name for signIn function for compatibility with auth.ts
 * @param formData - Form data containing email and password
 * @returns Promise with the authentication response
 */
export const logIn = signIn;

/**
 * Retrieves user data from the API
 * Also served to check logged in status
 * @returns Promise with the user data
 */
const dummyUserData: UserData = {
  UUID: "localdev",
  firstName: "Local",
  lastName: "Development",
  eMail: "anon@trinckle.com",
  license: { startDate: "0", endDate: "999999999", type: "Free" },
  organization: "Trinckle Dev",
};

const _getUserDataImpl = async (): Promise<AuthResponse> => {
  // onprem: the host server is expected to provide data/userData.json
  if (currentEnv === 'onprem') {
    const userData = await fetchResource<UserData>('data/userData.json');
    const u = userData ?? dummyUserData;
    liveAppData.userData = u;
    liveAppData.isAuthenticated = true;
    if (userData) {
      window.dispatchEvent(new CustomEvent('as-auth-update', {
        detail: { isAuthenticated: true, userData: u }
      }));
    }
    return { isAuthenticated: true, isDummyData: true, userData: u, success: true };
  }

  // localDev: skip the network fetch — the file rarely exists in local setups and
  // the 404 shows in the browser console even when JS handles it gracefully
  if (localDev) {
    liveAppData.userData = dummyUserData;
    liveAppData.isAuthenticated = true;
    return { isAuthenticated: true, isDummyData: true, userData: dummyUserData, success: true };
  }

  try {
    const response = await apiFetch(`${apiRoot}?function=getUserData`);
    const data = await validatedServerResponse(response);
    // devlog('getUserData response', data);

    if (!data.success) {
      // Update the store directly
      liveAppData.userData = null;
      liveAppData.isAuthenticated = false;

      // Also dispatch event for backward compatibility
      window.dispatchEvent(new CustomEvent('as-auth-update', {
        detail: {
          isAuthenticated: false,
          userData: null
        }
      }));

      return {
        isAuthenticated: false,
        userData: null,
        success: false,
        message: data.message || data.errorMessage || 'Failed to get user data'
      };
    }

    // Update the store directly
    liveAppData.userData = data.data;
    liveAppData.isAuthenticated = true;

    // Also dispatch event for backward compatibility
    window.dispatchEvent(new CustomEvent('as-auth-update', {
      detail: {
        isAuthenticated: true,
        userData: data.data
      }
    }));

    return {
      isAuthenticated: true,
      userData: data.data,
      success: true
    };
  } catch (error) {
    console.error('getUserData error:', error.message);

    // Update the store directly
    liveAppData.userData = null;
    liveAppData.isAuthenticated = false;

    // Also dispatch event for backward compatibility
    window.dispatchEvent(new CustomEvent('as-auth-update', {
      detail: {
        isAuthenticated: false,
        userData: null
      }
    }));

    return {
      isAuthenticated: false,
      userData: null,
      success: false,
      message: error.message || 'Failed to get user data'
    };
  }
};

export const getUserData = (): Promise<AuthResponse> => {
  if (!_getUserDataInFlight) {
    _getUserDataInFlight = _getUserDataImpl().finally(() => { _getUserDataInFlight = null; });
  }
  return _getUserDataInFlight;
};

export const getDummyUserData = async (succeed = true): Promise<AuthResponse> => {
  return new Promise<AuthResponse>((resolve) => {
    setTimeout(() => {
      const userData: UserData = {
        UUID: 'dev-user-uuid-123',
        firstName: 'Dev',
        lastName: 'User',
        eMail: 'dev@trinckle.com',
        license: {
          type: 'free',
          startDate: '2025-02-12 00:00:00',
          endDate: '2025-07-15 00:00:00',
          applications: [
            'roboticFingers',
            'workpieceCreator',
            'clamping'
          ]
        },
        organization: 'Trinckle GmbH'
      }

      // Update the store directly
      liveAppData.userData = succeed ? userData : null;
      liveAppData.isAuthenticated = succeed;

      // Also dispatch event for backward compatibility
      window.dispatchEvent(new CustomEvent('as-auth-update', {
        detail: {
          isAuthenticated: succeed,
          userData: succeed ? userData : null
        }
      }));

      resolve({
        isAuthenticated: succeed,
        userData: succeed ? userData : null,
        success: succeed
      });
    }, 500);
  });
};


export const logOut = async () => {
  try {
    const response = await apiFetch(`${apiRoot}?function=logout`, {
      method: 'POST',
    });

    const data = await validatedServerResponse(response);

    if (!data.success) {
      throw new Error(data.message || data.errorMessage || 'Logout failed');
    }

    liveAppData.userData = null;
    liveAppData.isAuthenticated = false;
    window.location.replace(window.location.pathname + window.location.hash);
  } catch (error) {
    console.error('logout failed:', error.message);
    window.dispatchEvent(new CustomEvent('as-notify', {
      bubbles: true,
      composed: true,
      detail: {
        defaultText: 'Sign out failed. Please try again.',
        type: 'error'
      }
    }));
  }
};



/**
 * Changes the user's password
 * @param oldPassword - Current password
 * @param newPassword - New password
 * @param confirmPassword - Confirmation of new password
 * @returns Promise with the response
 */
export const changePassword = async (
  oldPassword: string,
  newPassword: string,
  confirmPassword: string
): Promise<{ success: boolean; data?: any; errorCode?: number; errorMessage?: string }> => {
  try {
    const formData = new FormData();
    formData.append('oldPassword', oldPassword);
    formData.append('newPassword', newPassword);
    formData.append('confirmPassword', confirmPassword);

    const response = await apiFetch(`${apiRoot}?function=changePassword`, {
      method: 'POST',
      body: formData,
    });

    const data = await validatedServerResponse(response);

    if (!data.success) {
      return {
        success: false,
        errorCode: data.errorCode,
        errorMessage: data.errorMessage || data.message || 'Password change failed'
      };
    }

    return data;
  } catch (error) {
    console.error('changePassword error:', error.message);
    return {
      success: false,
      errorMessage: error.message || 'Password change failed'
    };
  }
};

/**
 * Requests a password recovery code via email
 * @param email - User's email address
 * @returns Promise with the response
 */
export const lostPassword = async (
  email: string
): Promise<{ success: boolean; data?: any; errorCode?: number; errorMessage?: string }> => {
  try {
    const formData = new FormData();
    formData.append('eMail', email);

    const response = await apiFetch(`${apiRoot}?function=lostPassword`, {
      method: 'POST',
      body: formData,
    });

    const data = await validatedServerResponse(response);

    if (!data.success) {
      return {
        success: false,
        errorCode: data.errorCode,
        errorMessage: data.errorMessage || data.message || 'Password recovery request failed'
      };
    }

    return data;
  } catch (error) {
    console.error('lostPassword error:', error.message);
    return {
      success: false,
      errorMessage: error.message || 'Password recovery request failed'
    };
  }
};

/**
 * Checks the validity of a password recovery code
 * @param code - Password recovery code
 * @returns Promise with the response
 */
export const checkRegainCode = async (
  code: string
): Promise<{ success: boolean; data?: any; errorCode?: number; errorMessage?: string }> => {
  try {
    const formData = new FormData();
    formData.append('code', code);

    const response = await apiFetch(`${apiRoot}?function=checkRegainCode`, {
      method: 'POST',
      body: formData,
    });

    const data = await validatedServerResponse(response);

    if (!data.success) {
      return {
        success: false,
        errorCode: data.errorCode,
        errorMessage: data.errorMessage || data.message || 'Recovery code validation failed'
      };
    }

    return data;
  } catch (error) {
    console.error('checkRegainCode error:', error.message);
    return {
      success: false,
      errorMessage: error.message || 'Recovery code validation failed'
    };
  }
};

/**
 * Resets password and regains account access using a recovery code
 * Also logs in the user and sets the session cookie
 * @param code - Password recovery code
 * @param newPassword - New password
 * @param confirmPassword - Confirmation of new password
 * @returns Promise with the response
 */
export const regainAccess = async (
  code: string,
  newPassword: string,
  confirmPassword: string
): Promise<{ success: boolean; data?: any; errorCode?: number; errorMessage?: string }> => {
  try {
    const formData = new FormData();
    formData.append('code', code);
    formData.append('newPassword', newPassword);
    formData.append('confirmPassword', confirmPassword);

    const response = await apiFetch(`${apiRoot}?function=regainAccess`, {
      method: 'POST',
      body: formData,
    });

    const data = await validatedServerResponse(response);

    if (!data.success) {
      return {
        success: false,
        errorCode: data.errorCode,
        errorMessage: data.errorMessage || data.message || 'Account recovery failed'
      };
    }

    // If successful, get user data and update store
    await getUserData();

    return data;
  } catch (error) {
    console.error('regainAccess error:', error.message);
    return {
      success: false,
      errorMessage: error.message || 'Account recovery failed'
    };
  }
};

/**
 * Registers a new user
 * @param email - User's email
 * @param firstName - User's first name
 * @param lastName - User's last name
 * @param organizationName - User's organization name
 * @param captcha - Captcha verification
 * @param marketingOptIn - Marketing opt-in flag
 * @returns Promise with the response
 */
export const register = async (
  email: string,
  firstName: string,
  lastName: string,
  organizationName: string,
  captcha: string,
  marketingOptIn: boolean,
  marketingSource?: string
): Promise<{ success: boolean; data?: any; errorCode?: number; errorMessage?: string }> => {
  try {
    const formData = new FormData();
    formData.append('eMail', email);
    formData.append('firstName', firstName);
    formData.append('lastName', lastName);
    formData.append('organizationName', organizationName);
    formData.append('captcha', captcha);
    formData.append('marketingOptIn', marketingOptIn ? '1' : '0');
    if (marketingSource) formData.append('marketingSource', marketingSource);

    const response = await apiFetch(`${apiRoot}?function=register`, {
      method: 'POST',
      body: formData,
    });

    const data = await validatedServerResponse(response);

    if (!data.success) {
      return {
        success: false,
        errorCode: data.errorCode,
        errorMessage: data.errorMessage || data.message || 'Registration failed'
      };
    }

    return data;
  } catch (error) {
    console.error('register error:', error.message);
    return {
      success: false,
      errorMessage: error.message || 'Registration failed'
    };
  }
};

/**
 * Checks the validity of an account activation code
 * @param code - Account activation code
 * @returns Promise with the response
 */
export const checkActivationCode = async (
  code: string
): Promise<{ success: boolean; data?: any; errorCode?: number; errorMessage?: string }> => {
  try {
    const formData = new FormData();
    formData.append('code', code);

    const response = await apiFetch(`${apiRoot}?function=checkActivationCode`, {
      method: 'POST',
      body: formData,
    });

    const data = await validatedServerResponse(response);

    if (!data.success) {
      return {
        success: false,
        errorCode: data.errorCode,
        errorMessage: data.errorMessage || data.message || 'Activation code validation failed'
      };
    }

    return data;
  } catch (error) {
    console.error('checkActivationCode error:', error.message);
    return {
      success: false,
      errorMessage: error.message || 'Activation code validation failed'
    };
  }
};

/**
 * Activates a user account after registration
 * Also logs in the user and sets the session cookie
 * @param code - Account activation code
 * @param password - User's chosen password
 * @param confirmPassword - Confirmation of password
 * @returns Promise with the response
 */
export const activateUser = async (
  code: string,
  password: string,
  confirmPassword: string
): Promise<{ success: boolean; data?: any; errorCode?: number; errorMessage?: string }> => {
  try {
    const formData = new FormData();
    formData.append('code', code);
    formData.append('password', password);
    formData.append('confirmPassword', confirmPassword);

    const response = await apiFetch(`${apiRoot}?function=activateUser`, {
      method: 'POST',
      body: formData,
    });

    const data = await validatedServerResponse(response);

    if (!data.success) {
      return {
        success: false,
        errorCode: data.errorCode,
        errorMessage: data.errorMessage || data.message || 'Account activation failed'
      };
    }

    // If successful, get user data and update store
    await getUserData();

    return data;
  } catch (error) {
    console.error('activateUser error:', error.message);
    return {
      success: false,
      errorMessage: error.message || 'Account activation failed'
    };
  }
};

/**
 * Gets the user's license data
 * @returns Promise with the license data
 */
export const getLicenseData = async (): Promise<{
  success: boolean;
  data?: any;
  errorCode?: number;
  errorMessage?: string
}> => {
  try {
    const response = await apiFetch(`${apiRoot}?function=getLicenseData`);
    const data = await validatedServerResponse(response);

    if (!data.success) {
      return {
        success: false,
        errorCode: data.errorCode,
        errorMessage: data.errorMessage || data.message || 'Failed to get license data'
      };
    }

    return data;
  } catch (error) {
    console.error('getLicenseData error:', error.message);
    return {
      success: false,
      errorMessage: error.message || 'Failed to get license data'
    };
  }
};

/**
 * Utility function to check if server response is OK
 * @param response - Server response object
 * @returns boolean indicating if response was successful
 */
export const serverOK = (response: any): boolean => {
  return response.success;
};
export const requestSucceeded = async (response: Response): Promise<any> => {
  return new Promise(async (resolve, reject) => {
    devlog(response)
    const resText = await response.text();
    try {
      const resData = JSON.parse(resText)
      if (resData.success) {
        resolve(resData);
      } else {
        reject({
          errorCode: resData.errorCode,
          errorMessage: resData.errorMessage
        });
      }
    } catch (err) {
      reject(`Response not valid json. Response: ${resText}`)
    }
  })
}

export const getOrgUserList = async (dev: boolean = false): Promise<{
  success: boolean; data?: {
    UUID: string;
    firstName: string;
    lastName: string;
    eMail: string; // Email field from API response
    status: 'A' | 'I'; // A = active, I = inactive
  }; errorCode?: number; errorMessage?: string
}> => {
  try {
    const response = await apiFetch(`${dev ? `/api` : apiRoot}?function=listUsersOfCompany`);
    const data = await validatedServerResponse(response);
    return data;
  } catch (error) {
    console.error('getOrgUserList error:', error.message);
  }
}

export const sendOrgInvite = async (email: string, firstName: string, lastName: string, dev: boolean = false): Promise<{ success: boolean; data?: any; errorCode?: number; errorMessage?: string }> => {
  try {
    const formData = new FormData();
    formData.append('eMail', email);
    formData.append('firstName', firstName);
    formData.append('lastName', lastName);

    const response = await apiFetch(`${dev ? `/api` : apiRoot}?function=invite`, {
      method: 'POST',
      body: formData,
    });
    const data = await validatedServerResponse(response);
    return data;
  } catch (error) {
    console.error('sendOrgInvite error:', error.message);
  }
}

export const resendActivationCode = async (email: string, dev: boolean = false): Promise<{ success: boolean; data?: any; errorCode?: number; errorMessage?: string }> => {
  try {
    const formData = new FormData();
    formData.append('eMail', email);
    const response = await apiFetch(`${dev ? `/api` : apiRoot}?function=resendActivation`, {
      method: 'POST',
      body: formData,
    });
    const data = await validatedServerResponse(response);
    return data;
  } catch (error) {
    console.error('resendActivationCode error:', error.message);
  }
}



export const resendOrgInvite = async (email: string, dev: boolean = false): Promise<{ success: boolean; data?: any; errorCode?: number; errorMessage?: string }> => {
  try {
    const formData = new FormData();
    formData.append('eMail', email);
    const response = await apiFetch(`${dev ? `/api` : apiRoot}?function=resendInvitation`, {
      method: 'POST',
      body: formData,
    });
    const data = await validatedServerResponse(response);
    return data;
  } catch (error) {
    console.error('resendOrgInvite error:', error.message);
  }
}


export const AsApi = {
  apiRoot,
  signIn,
  getUserData,
  logOut,
  getDummyUserData,
  changePassword,
  lostPassword,
  checkRegainCode,
  regainAccess,
  register,
  checkActivationCode,
  activateUser,
  getLicenseData,
  serverOKd: serverOK,
  // Legacy functions for backward compatibility
  doLogin,
  doLogout,

  resendActivationCode,
  getOrgUserList,
  sendOrgInvite,
  resendOrgInvite,
  sendLogToServer,
  sendBeaconToServer,
  fetchResource,
  serverCodes
}